/**
 * /activate — UDISE+ based real 14-day FREE Trial activation.
 *
 * Flow: enter UDISE code -> fetch from master DB -> verify/edit (or manual)
 *       -> start trial -> redirect to dashboard with real-data, writes enabled.
 *
 * Replaces the old looping /payment "Activate" path.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { FoundingSeatsCounter } from '../components/FoundingSeatsCounter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ActivatePage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  // Activation link is sent to (Hindi-speaking) school admins via WhatsApp, so this
  // standalone page is Hindi-first unless the user has explicitly chosen English.
  const effLang = (language === 'en' && localStorage.getItem('language') === 'en') ? 'en' : 'hi';
  const L = (en, hi) => (effLang === 'hi' ? hi : en);

  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [step, setStep] = useState('code'); // phone | code | verify | done
  const [udise, setUdise] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [manual, setManual] = useState(false);
  const [form, setForm] = useState({
    school_name: '', board: 'CBSE', principal_name: '', phone: '', email: '',
    district: '', state: '', address: '', established_year: '',
  });
  const [trialInfo, setTrialInfo] = useState(null);
  // Self-activation (no prior login) — phone + OTP
  const [selfSignup] = useState(() => !localStorage.getItem('token'));
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [signupName, setSignupName] = useState('');

  useEffect(() => {
    if (!token) setStep('phone');
  }, [token]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const sendOtp = async () => {
    setError(''); setInfo('');
    const p = phone.trim();
    if (!/^\d{10}$/.test(p)) {
      setError(L('Enter a valid 10-digit phone number.', '10 अंकों का सही फ़ोन नंबर डालें।'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/otp/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: p }),
      });
      const data = await res.json();
      if (res.ok && data.success !== false) {
        setOtpSent(true);
        setInfo(L('OTP sent on WhatsApp/SMS. Enter it below.', 'OTP आपके WhatsApp/SMS पर भेज दिया — नीचे डालें।'));
      } else {
        setError(data.detail || L('Could not send OTP.', 'OTP नहीं भेज पाए।'));
      }
    } catch {
      setError(L('Something went wrong. Please try again.', 'कुछ गड़बड़ हुई। दोबारा कोशिश करें।'));
    }
    setLoading(false);
  };

  const verifyAndProvision = async () => {
    setError(''); setInfo('');
    const p = phone.trim();
    if (!/^\d{6}$/.test(otp.trim())) {
      setError(L('Enter the 6-digit OTP.', '6 अंकों का OTP डालें।'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/activation/self-start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: p, otp: otp.trim(), name: signupName.trim() || null }),
      });
      const data = await res.json();
      if (res.ok && data.success && data.access_token) {
        localStorage.setItem('token', data.access_token);
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        setToken(data.access_token);
        setF('phone', p);
        setInfo('');
        setStep('code');
      } else {
        setError(data.detail || L('Verification failed.', 'Verify नहीं हो पाया।'));
      }
    } catch {
      setError(L('Something went wrong. Please try again.', 'कुछ गड़बड़ हुई। दोबारा कोशिश करें।'));
    }
    setLoading(false);
  };

  const lookup = async () => {
    setError(''); setInfo('');
    const code = udise.trim();
    if (!/^\d{11}$/.test(code)) {
      setError(L('UDISE code must be exactly 11 digits.', 'UDISE code ठीक 11 अंकों का होना चाहिए।'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/activation/udise-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ udise_code: code }),
      });
      const data = await res.json();
      if (data.found && data.preview) {
        const p = data.preview;
        setForm({
          school_name: p.school_name || '', board: p.board || 'CBSE',
          principal_name: p.principal_name || '', phone: p.phone || '', email: p.email || '',
          district: p.district || '', state: p.state || '', address: p.address || '',
          established_year: p.established_year ? String(p.established_year) : '',
        });
        setManual(false);
        setInfo(L(data.message_en || 'School found! Verify the details below.', data.message || 'School मिल गई! नीचे details verify करें।'));
      } else {
        setManual(true);
        setInfo(L(data.message_en || 'Not found — fill the details manually to start your trial.',
          data.message || 'नहीं मिली — details खुद भरकर trial शुरू करें।'));
      }
      setStep('verify');
    } catch {
      setError(L('Something went wrong. Please try again.', 'कुछ गड़बड़ हुई। दोबारा कोशिश करें।'));
    }
    setLoading(false);
  };

  const skipManual = () => {
    setManual(true); setInfo(''); setError(''); setStep('verify');
  };

  const startTrial = async () => {
    setError('');
    if (!form.school_name || form.school_name.trim().length < 2) {
      setError(L('Please enter the school name.', 'कृपया school का नाम भरें।'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/activation/start-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ udise_code: manual ? null : udise.trim() || null, manual, ...form }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        setError(data.detail || L('Could not start trial.', 'Trial शुरू नहीं हो सका।'));
        setLoading(false);
        return;
      }
      // Mark the locally-stored user as no longer demo so UI gating updates.
      try {
        const u = JSON.parse(localStorage.getItem('user') || 'null');
        if (u) { u.is_demo = false; u.account_state = 'trial'; localStorage.setItem('user', JSON.stringify(u)); }
      } catch { /* */ }
      setTrialInfo(data);
      setStep('done');
    } catch {
      setError(L('Something went wrong. Please try again.', 'कुछ गड़बड़ हुई। दोबारा कोशिश करें।'));
    }
    setLoading(false);
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(effLang === 'hi' ? 'hi-IN' : 'en-IN', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50 py-10 px-4" data-testid="activate-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold mb-3">
            🎁 {L('14-Day FREE Trial · Founding one-time ₹2499', '14-दिन FREE Trial · Founding one-time ₹2499')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900">
            {L('Activate Your School', 'अपनी School Activate करें')}
          </h1>
          <p className="text-slate-600 mt-2 text-sm sm:text-base">
            {L('Start your 14-day free trial with your own real data — no payment now.',
               'अपने असली data के साथ 14-दिन का free trial शुरू करें — अभी कोई payment नहीं।')}
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 mb-6 text-xs font-semibold">
          {(selfSignup
            ? [['phone', L('Verify Phone', 'फ़ोन Verify')], ['code', L('UDISE Code', 'UDISE Code')], ['verify', L('Verify', 'Verify')], ['done', L('Done', 'पूरा')]]
            : [['code', L('UDISE Code', 'UDISE Code')], ['verify', L('Verify', 'Verify')], ['done', L('Done', 'पूरा')]]
          ).map(([k, label], i, arr) => (
            <React.Fragment key={k}>
              <span className={`px-3 py-1 rounded-full ${step === k ? 'bg-orange-500 text-white' : (arr.map(a => a[0]).indexOf(step) > i ? 'bg-emerald-500 text-white' : 'bg-white text-slate-500 border border-slate-200')}`}>{i + 1}. {label}</span>
              {i < arr.length - 1 && <span className="text-slate-300">—</span>}
            </React.Fragment>
          ))}
        </div>

        <div className="max-w-md mx-auto mb-5"><FoundingSeatsCounter /></div>

        <div className="bg-white rounded-2xl shadow-xl border border-orange-100 overflow-hidden">
          {error && <div className="bg-rose-50 text-rose-700 text-sm px-5 py-3 border-b border-rose-100" data-testid="activate-error">{error}</div>}
          {info && !error && <div className="bg-emerald-50 text-emerald-700 text-sm px-5 py-3 border-b border-emerald-100" data-testid="activate-info">{info}</div>}

          {/* STEP 0 — phone + OTP (only for brand-new leads without login) */}
          {step === 'phone' && (
            <div className="p-6 space-y-4" data-testid="activate-phone-step">
              <p className="text-sm font-bold text-slate-700">{L('Verify your WhatsApp number to begin', 'शुरू करने के लिए अपना WhatsApp नंबर verify करें')}</p>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">{L('Your Name', 'आपका नाम')}</label>
                <input value={signupName} onChange={(e) => setSignupName(e.target.value)} disabled={otpSent}
                  placeholder={L('e.g. Ramesh Sharma', 'जैसे रमेश शर्मा')}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 outline-none text-sm disabled:bg-slate-50" data-testid="signup-name-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">{L('WhatsApp Number (10 digits)', 'WhatsApp नंबर (10 अंक)')}</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} disabled={otpSent}
                  placeholder="9876543210" inputMode="numeric"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-lg tracking-wide font-mono disabled:bg-slate-50" data-testid="signup-phone-input" />
              </div>

              {!otpSent ? (
                <button onClick={sendOtp} disabled={loading}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:to-orange-600 text-white font-bold text-lg disabled:opacity-50 transition shadow-lg shadow-orange-200" data-testid="send-otp-btn">
                  {loading ? L('Sending…', 'भेज रहे हैं…') : L('Send OTP', 'OTP भेजें')}
                </button>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">{L('Enter 6-digit OTP', '6 अंकों का OTP डालें')}</label>
                    <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="• • • • • •" inputMode="numeric"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-lg tracking-[0.4em] text-center font-mono" data-testid="signup-otp-input" />
                  </div>
                  <button onClick={verifyAndProvision} disabled={loading}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:to-emerald-700 text-white font-bold text-lg disabled:opacity-50 transition shadow-lg shadow-emerald-200" data-testid="verify-otp-btn">
                    {loading ? L('Verifying…', 'Verify कर रहे हैं…') : L('Verify & Continue', 'Verify करके आगे बढ़ें')}
                  </button>
                  <button onClick={() => { setOtpSent(false); setOtp(''); setInfo(''); setError(''); }}
                    className="w-full text-sm text-slate-500 hover:text-orange-600 underline" data-testid="signup-change-number">
                    {L('Change number / Resend', 'नंबर बदलें / दोबारा भेजें')}
                  </button>
                </>
              )}
              <p className="text-center text-xs text-slate-400">
                {L('Already have a login?', 'पहले से login है?')}{' '}
                <button onClick={() => navigate('/login?next=/activate')} className="text-orange-600 underline" data-testid="goto-login-link">{L('Login here', 'यहाँ Login करें')}</button>
              </p>
            </div>
          )}

          {/* STEP 1 — code */}
          {step === 'code' && (
            <div className="p-6 space-y-4">
              <label className="block text-sm font-bold text-slate-700">{L('Enter your 11-digit UDISE+ Code', 'अपना 11-अंकों का UDISE+ Code डालें')}</label>
              <input
                value={udise}
                onChange={(e) => setUdise(e.target.value.replace(/\D/g, '').slice(0, 11))}
                placeholder={L('e.g. 23260505717', 'जैसे 23260505717')}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-lg tracking-wide font-mono"
                data-testid="udise-input"
                inputMode="numeric"
              />
              <button
                onClick={lookup}
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:to-orange-600 text-white font-bold text-lg disabled:opacity-50 transition shadow-lg shadow-orange-200"
                data-testid="udise-lookup-btn"
              >
                {loading ? L('Searching…', 'खोज रहे हैं…') : `🔍 ${L('Find My School', 'मेरी School खोजें')}`}
              </button>
              <button onClick={skipManual} className="w-full text-sm text-slate-500 hover:text-orange-600 underline" data-testid="udise-skip-btn">
                {L("I don't have a UDISE code — fill details manually", 'मेरे पास UDISE code नहीं है — details खुद भरूँगा')}
              </button>
            </div>
          )}

          {/* STEP 2 — verify / manual */}
          {step === 'verify' && (
            <div className="p-6 space-y-4">
              <p className="text-sm font-bold text-slate-700">
                {manual ? L('Fill your school details', 'अपनी school की details भरें')
                        : L('Verify & edit your school details', 'अपनी school की details verify/edit करें')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  ['school_name', L('School Name', 'School का नाम'), true],
                  ['board', L('Board', 'Board'), false],
                  ['principal_name', L('Principal Name', 'Principal का नाम'), false],
                  ['phone', L('Phone', 'Phone'), false],
                  ['email', L('Email', 'Email'), false],
                  ['district', L('District', 'जिला'), false],
                  ['state', L('State', 'राज्य'), false],
                  ['established_year', L('Established Year', 'स्थापना वर्ष'), false],
                ].map(([k, label, req]) => (
                  <div key={k} className={k === 'school_name' ? 'sm:col-span-2' : ''}>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">{label}{req && ' *'}</label>
                    <input
                      value={form[k]}
                      onChange={(e) => setF(k, e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 outline-none text-sm"
                      data-testid={`activate-field-${k}`}
                    />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">{L('Address', 'पता')}</label>
                  <input value={form.address} onChange={(e) => setF('address', e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 outline-none text-sm" data-testid="activate-field-address" />
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                ⚠️ {L('Starting the trial will clear the sample demo data and set up your school with real, editable data.',
                       'Trial शुरू करते ही नकली demo data हट जाएगा और आपकी school असली, editable data के साथ set हो जाएगी।')}
              </div>

              <div className="flex gap-2">
                <button onClick={() => { setStep('code'); setError(''); setInfo(''); }} className="px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-sm" data-testid="activate-back-btn">
                  ← {L('Back', 'वापस')}
                </button>
                <button onClick={startTrial} disabled={loading} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:to-emerald-700 text-white font-bold disabled:opacity-50 transition shadow-lg shadow-emerald-200" data-testid="start-trial-btn">
                  {loading ? L('Starting…', 'शुरू कर रहे हैं…') : `🚀 ${L('Start 14-Day Free Trial', '14-दिन Free Trial शुरू करें')}`}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — done */}
          {step === 'done' && trialInfo && (
            <div className="p-8 text-center space-y-4" data-testid="activate-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-3xl">🎉</div>
              <h2 className="text-2xl font-black text-slate-900">{L('Trial Activated!', 'Trial Active हो गया!')}</h2>
              <p className="text-slate-600 text-sm">
                {L('Your 14-day free trial is live. Add your real students, mark attendance, and explore everything.',
                   'आपका 14-दिन free trial चालू है। अपने असली students जोड़ें, attendance लगाएँ, और सब कुछ explore करें।')}
              </p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 inline-block">
                <p className="text-xs text-emerald-600 font-semibold">{L('Trial ends on', 'Trial खत्म होगा')}</p>
                <p className="text-lg font-bold text-emerald-700">{fmtDate(trialInfo.trial_expires_at)}</p>
              </div>
              <button onClick={() => navigate('/dashboard', { replace: true })} className="w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:to-orange-600 text-white font-bold text-lg transition shadow-lg shadow-orange-200" data-testid="go-dashboard-btn">
                {L('Go to Dashboard', 'Dashboard पर जाएँ')} →
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-5">🚀 शिक्षा मित्र · {L('No payment required to start the trial', 'Trial शुरू करने के लिए कोई payment नहीं')}</p>
      </div>
    </div>
  );
}
